import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OTHER_KEY = '__OTHER__';

/**
 * Shared Service / Work Category picker — used by Supplier Registration
 * AND Buyer Tender / Quick Quotation forms.
 *
 * Props
 *   value           : string[]   — selected category names
 *   onChange        : (string[]) => void
 *   multiple        : boolean    — default true (single-select for Buyer flows)
 *   allowOther      : boolean    — default true
 *   required        : boolean    — visual hint only
 *   testid          : string
 *   label           : string
 *   placeholder     : string
 *   onCategoryAdded : (newCategoryName: string) => void   (optional notify)
 */
const CategoryMultiSelect = ({
  value = [],
  onChange,
  multiple = true,
  allowOther = true,
  required = false,
  testid = 'category-multiselect',
  label = 'Service / Work Categories',
  placeholder = 'Select one or more categories…',
  onCategoryAdded,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [otherName, setOtherName] = useState('');
  const [proposing, setProposing] = useState(false);
  const wrapperRef = useRef(null);

  // Selected[] always holds canonical category names (strings).
  const selected = useMemo(() => (Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : [])), [value]);
  const showOtherField = allowOther && selected.includes(OTHER_KEY);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-fatal — propagate empty list so the user still gets the "Other" path.
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  // Close on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const visibleCats = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter(c => (c.name || '').toLowerCase().includes(term));
  }, [categories, search]);

  const toggle = (name) => {
    if (!multiple) {
      onChange([name]);
      setOpen(false);
      return;
    }
    if (selected.includes(name)) {
      onChange(selected.filter(s => s !== name));
    } else {
      onChange([...selected, name]);
    }
  };

  const toggleOther = () => {
    if (selected.includes(OTHER_KEY)) {
      onChange(selected.filter(s => s !== OTHER_KEY));
      setOtherName('');
    } else {
      onChange(multiple ? [...selected, OTHER_KEY] : [OTHER_KEY]);
    }
  };

  const remove = (name) => onChange(selected.filter(s => s !== name));

  const submitOther = async () => {
    const name = otherName.trim();
    if (name.length < 3) { toast.error('Category name must be at least 3 characters'); return; }
    setProposing(true);
    try {
      const { data } = await axios.post(`${API}/categories/propose`, { name }, { withCredentials: true });
      const canonical = data?.name || name;
      await fetchCategories();
      // Replace the OTHER placeholder with the canonical name.
      const next = selected.filter(s => s !== OTHER_KEY);
      if (!next.includes(canonical)) next.push(canonical);
      onChange(multiple ? next : [canonical]);
      setOtherName('');
      if (data?.duplicate) {
        toast.success(`"${canonical}" already exists — linked instead.`);
      } else {
        toast.success(`"${canonical}" added to the master category list.`);
        onCategoryAdded && onCategoryAdded(canonical);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    } finally {
      setProposing(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={`${testid}-wrapper`}>
      {label && (
        <label className="block text-sm font-medium mb-2">
          {label}{required && <span className="text-red-600 ml-0.5">*</span>}
        </label>
      )}

      {/* Selected chips */}
      <div
        className="min-h-[44px] w-full px-3 py-2 rounded-md border border-input bg-background flex flex-wrap gap-1.5 items-center cursor-text"
        onClick={() => setOpen(true)}
        data-testid={`${testid}-control`}
      >
        {selected.length === 0 && (
          <span className="text-sm text-muted-foreground" data-testid={`${testid}-placeholder`}>{placeholder}</span>
        )}
        {selected.map(name => (
          <span
            key={name}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${name === OTHER_KEY ? 'bg-orange/15 text-orange' : 'bg-navy/10 text-navy'}`}
            data-testid={`${testid}-chip-${(name || '').replace(/\s+/g, '-').toLowerCase()}`}
          >
            {name === OTHER_KEY ? (<><Sparkles className="h-3 w-3" /> Other (specify below)</>) : name}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); name === OTHER_KEY ? toggleOther() : remove(name); }}
              className="hover:opacity-70"
              aria-label={`Remove ${name}`}
              data-testid={`${testid}-remove-${(name || '').replace(/\s+/g, '-').toLowerCase()}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className="ml-auto text-muted-foreground hover:text-navy"
          aria-label="Toggle category list"
          data-testid={`${testid}-toggle`}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-72 overflow-auto"
          data-testid={`${testid}-dropdown`}
        >
          <div className="p-2 border-b border-border sticky top-0 bg-white">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search categories…"
              className="w-full px-2 py-1.5 text-sm rounded border border-input"
              data-testid={`${testid}-search`}
            />
          </div>
          {loading && <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>}
          {!loading && visibleCats.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No categories match &quot;{search}&quot;.</div>
          )}
          {!loading && visibleCats.map(cat => {
            const isSel = selected.includes(cat.name);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggle(cat.name)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between ${isSel ? 'bg-navy/5' : ''}`}
                data-testid={`${testid}-option-${(cat.name || '').replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className="flex flex-col">
                  <span className="font-medium">{cat.name}</span>
                  {cat.source === 'user_proposed' && <span className="text-[10px] text-orange">Community-added</span>}
                </span>
                {isSel && <Check className="h-4 w-4 text-navy" />}
              </button>
            );
          })}
          {allowOther && (
            <button
              type="button"
              onClick={() => { toggleOther(); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between border-t border-border ${selected.includes(OTHER_KEY) ? 'bg-orange/5' : ''}`}
              data-testid={`${testid}-option-other`}
            >
              <span className="flex items-center gap-1.5 font-medium text-orange">
                <Sparkles className="h-3.5 w-3.5" /> Other — propose a new category
              </span>
              {selected.includes(OTHER_KEY) && <Check className="h-4 w-4 text-orange" />}
            </button>
          )}
        </div>
      )}

      {/* "Other" specify field appears immediately below the picker */}
      {showOtherField && (
        <div className="mt-3 p-3 rounded-md border-2 border-dashed border-orange/40 bg-orange/5" data-testid={`${testid}-other-block`}>
          <label className="block text-xs font-bold text-navy mb-1.5">
            Please specify your Service / Work Category
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={otherName}
              onChange={(e) => setOtherName(e.target.value)}
              placeholder="e.g. Solar Panel Cleaning, Facade Glass Cleaning, EV Charging Maintenance…"
              className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm"
              maxLength={80}
              data-testid={`${testid}-other-input`}
            />
            <button
              type="button"
              onClick={submitOther}
              disabled={proposing || otherName.trim().length < 3}
              className="px-4 py-2 rounded-md bg-orange hover:bg-orange/90 text-white text-sm font-semibold disabled:opacity-50"
              data-testid={`${testid}-other-submit`}
            >
              {proposing ? 'Adding…' : 'Add Category'}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            New categories become immediately available for all suppliers and buyers across ProcureOne Ultra.
          </p>
        </div>
      )}
    </div>
  );
};

export default CategoryMultiSelect;
export { OTHER_KEY };
